import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, X, Loader2, ShieldAlert, Trash2, Smartphone, PackagePlus, Undo2, ArrowDownToLine, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

// Venda sai só pela linha vermelha (vendas detectadas no ERP).
const TIPOS_BAIXA = [
  { valor: 'devolvido', label: 'Devolução ao fornecedor' },
  { valor: 'defeito', label: 'Defeito / assistência' },
  { valor: 'outro', label: 'Outro' },
];

const STATUS_LABEL = {
  estoque: 'Em estoque',
  vendido: 'Vendido',
  devolvido: 'Devolvido',
  defeito: 'Defeito',
  outro: 'Outro',
};

const INTERVALO_SAIDAS_MS = 60000;

const NOMES_MES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500';

function hojeIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatarData(valor) {
  if (!valor) return '—';
  const [ano, mes, dia] = String(valor).slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

function corDiferenca(diferenca) {
  if (diferenca === 0) return 'bg-green-100 text-green-800';
  return 'bg-red-100 text-red-800';
}

// "IMEI1" ou "IMEI1;IMEI2" (também aceita vírgula, tab ou espaço) — um aparelho por linha
function lerListaImeis(texto) {
  return texto
    .split('\n')
    .map(linha => linha.trim())
    .filter(Boolean)
    .map((linha) => {
      const [imei, imei2] = linha.split(/[;,\t ]+/);
      return { imei, imei2: imei2 || null };
    });
}

function Modal({ titulo, onClose, children, largura = 'max-w-lg' }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-2xl ${largura} w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ============================================
// INCLUIR MODELO (busca no ERP)
// ============================================
function ModalIncluirModelo({ onClose, onIncluido, ordensAcompanhadas }) {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [incluindo, setIncluindo] = useState(null);

  async function handleBuscar(e) {
    e.preventDefault();
    if (termo.trim().length < 2) {
      toast.error('Digite ao menos 2 caracteres.');
      return;
    }
    setBuscando(true);
    try {
      const resp = await apiService.celularesEstoque.buscarProdutos(termo.trim());
      setResultados(resp.data.data ?? []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao buscar produtos no ERP.');
    } finally {
      setBuscando(false);
    }
  }

  async function handleIncluir(produto) {
    setIncluindo(produto.Ordem);
    try {
      await apiService.celularesEstoque.incluirModelo(produto.Ordem);
      toast.success(`${produto.Nome} incluído no acompanhamento.`);
      onIncluido();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao incluir modelo.');
    } finally {
      setIncluindo(null);
    }
  }

  return (
    <Modal titulo="Incluir modelo de celular" onClose={onClose} largura="max-w-2xl">
      <form onSubmit={handleBuscar} className="flex gap-2 mb-4">
        <input
          type="text" value={termo} onChange={(e) => setTermo(e.target.value)} autoFocus
          placeholder="Nome, código ou código de barras do produto no ERP" className={inputClass}
        />
        <button type="submit" disabled={buscando} className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-50">
          {buscando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar
        </button>
      </form>

      {resultados.length === 0 && !buscando && (
        <p className="text-sm text-gray-500 text-center py-6">Busque o produto cadastrado no ERP.</p>
      )}

      <ul className="divide-y divide-gray-100">
        {resultados.map((p) => {
          const jaIncluido = ordensAcompanhadas.has(p.Ordem);
          return (
            <li key={p.Ordem} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{p.Nome}</p>
                <p className="text-xs text-gray-500">Cód. {p.Codigo}{p.Codigo_Barras ? ` · ${p.Codigo_Barras}` : ''}</p>
              </div>
              <button
                onClick={() => handleIncluir(p)} disabled={jaIncluido || incluindo === p.Ordem}
                className="px-3 py-1.5 text-sm rounded-lg border border-indigo-600 text-indigo-700 hover:bg-indigo-50 disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent whitespace-nowrap"
              >
                {jaIncluido ? 'Já incluído' : incluindo === p.Ordem ? 'Incluindo...' : 'Incluir'}
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

// ============================================
// CADASTRAR APARELHOS (IMEIs)
// ============================================
function ModalCadastrarAparelhos({ modelo, onClose, onSalvo }) {
  const [textoImeis, setTextoImeis] = useState('');
  const [dataEntrada, setDataEntrada] = useState(hojeIso());
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);

  const aparelhos = lerListaImeis(textoImeis);

  async function handleSalvar() {
    if (aparelhos.length === 0) {
      toast.error('Informe ao menos 1 IMEI.');
      return;
    }
    setSalvando(true);
    try {
      const resp = await apiService.celularesEstoque.cadastrarAparelhos({
        ordemProduto: modelo.ordemProduto, aparelhos, dataEntrada, observacao,
      });
      toast.success(`${resp.data.total} aparelho(s) cadastrado(s).`);
      onSalvo();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao cadastrar aparelhos.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo={`Entrada de aparelhos — ${modelo.nome}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">IMEIs (um aparelho por linha)</label>
          <textarea
            value={textoImeis} onChange={(e) => setTextoImeis(e.target.value)} rows={8} autoFocus
            placeholder={'356789012345678\n356789012345679;356789012345680  ← IMEI 1 e IMEI 2 (dual chip)'}
            className={`${inputClass} font-mono text-sm`} disabled={salvando}
          />
          <p className="text-xs text-gray-500 mt-1">
            Pode usar o leitor de código de barras. O IMEI 2 é opcional (separe com ; ou espaço). {aparelhos.length} aparelho(s).
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data de entrada</label>
            <input type="date" value={dataEntrada} onChange={(e) => setDataEntrada(e.target.value)} className={inputClass} disabled={salvando} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
            <input type="text" value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={255} placeholder="Ex.: NF 1234" className={inputClass} disabled={salvando} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={salvando} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSalvar} disabled={salvando || aparelhos.length === 0} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Cadastrar {aparelhos.length || ''}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================
// BAIXA DE APARELHO
// ============================================
function ModalBaixa({ aparelho, onClose, onSalvo }) {
  const [status, setStatus] = useState('devolvido');
  const [dataSaida, setDataSaida] = useState(hojeIso());
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function handleSalvar() {
    const tipo = TIPOS_BAIXA.find(t => t.valor === status)?.label;
    if (!window.confirm(`Confirmar baixa do IMEI ${aparelho.imei} como "${tipo}"?`)) return;
    setSalvando(true);
    try {
      await apiService.celularesEstoque.baixarAparelho(aparelho.id, { status, dataSaida, motivo });
      toast.success('Baixa registrada.');
      onSalvo();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao registrar baixa.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo={`Baixa — IMEI ${aparelho.imei}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de saída</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass} disabled={salvando}>
              {TIPOS_BAIXA.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
            <input type="date" value={dataSaida} onChange={(e) => setDataSaida(e.target.value)} className={inputClass} disabled={salvando} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Observação</label>
          <input type="text" value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={255} className={inputClass} disabled={salvando} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={salvando} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSalvar} disabled={salvando} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Registrar baixa
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================
// LISTA DE APARELHOS (por modelo ou busca por IMEI)
// ============================================
function TabelaAparelhos({ aparelhos, mostrarModelo, podeEstornar, onBaixar, onEstornar }) {
  if (aparelhos.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-6">Nenhum aparelho encontrado.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
          <tr>
            {mostrarModelo && <th className="text-left px-3 py-2">Modelo</th>}
            <th className="text-left px-3 py-2">IMEI</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Entrada</th>
            <th className="text-left px-3 py-2">Saída</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {aparelhos.map(a => (
            <tr key={a.id}>
              {mostrarModelo && <td className="px-3 py-2">{a.descricao || a.ordem_produto}</td>}
              <td className="px-3 py-2 font-mono">
                {a.imei}{a.imei2 && <span className="block text-xs text-gray-500">{a.imei2}</span>}
              </td>
              <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a.status === 'estoque' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                  {STATUS_LABEL[a.status]}
                </span>
                {a.sequencia_venda_erp && <span className="block text-xs text-gray-500 mt-0.5">Venda {a.sequencia_venda_erp}</span>}
              </td>
              <td className="px-3 py-2" title={a.observacao_entrada || undefined}>{formatarData(a.data_entrada)}</td>
              <td className="px-3 py-2" title={a.motivo_saida || undefined}>{formatarData(a.data_saida)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {a.status === 'estoque' ? (
                  <button onClick={() => onBaixar(a)} className="px-2 py-1 text-xs rounded border border-indigo-600 text-indigo-700 hover:bg-indigo-50 inline-flex items-center gap-1">
                    <ArrowDownToLine className="w-3 h-3" /> Baixar
                  </button>
                ) : podeEstornar && (
                  <button onClick={() => onEstornar(a)} className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1">
                    <Undo2 className="w-3 h-3" /> Estornar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// SAÍDAS PENDENTES (vendas detectadas no ERP)
// ============================================
function LinhaSaidaPendente({ saida, onConcluida }) {
  const [imei, setImei] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function handleFinalizar(e) {
    e.preventDefault();
    if (!imei.trim()) {
      toast.error('Informe o IMEI do aparelho vendido.');
      return;
    }
    setSalvando(true);
    try {
      await apiService.celularesEstoque.concluirSaida(saida.id, imei);
      toast.success('Saída finalizada.');
      await onConcluida();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao finalizar saída.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={handleFinalizar} className="bg-red-100 border border-red-300 rounded-lg p-3 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <p className="font-medium text-red-900">{saida.descricao || `Produto ${saida.ordem_produto}`}</p>
        <p className="text-xs text-red-800">
          {saida.codigo_erp ? `Cód. ${saida.codigo_erp} · ` : ''}
          Venda {saida.sequencia_venda_erp || '—'} · {formatarData(saida.data_venda)}
        </p>
      </div>
      <input
        type="text" inputMode="numeric" value={imei} onChange={(e) => setImei(e.target.value)}
        placeholder="IMEI" aria-label="IMEI do aparelho vendido" disabled={salvando}
        className="w-full sm:w-52 px-3 py-2 border border-red-300 rounded-lg font-mono text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
      />
      <button type="submit" disabled={salvando} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
        {salvando && <Loader2 className="w-4 h-4 animate-spin" />} Finalizar saída
      </button>
    </form>
  );
}

function BlocoSaidasPendentes({ saidas, onConcluida }) {
  if (saidas.length === 0) return null;
  return (
    <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4">
      <h2 className="font-semibold text-red-800 flex items-center gap-2 mb-1">
        <AlertTriangle className="w-5 h-5" /> Saídas a finalizar ({saidas.length})
      </h2>
      <p className="text-sm text-red-700 mb-3">Celulares vendidos no ERP. Informe o IMEI do aparelho para dar a saída na data da venda.</p>
      <div className="space-y-2">
        {saidas.map(s => <LinhaSaidaPendente key={s.id} saida={s} onConcluida={onConcluida} />)}
      </div>
    </div>
  );
}

// ============================================
// ABA ESTOQUE GERAL
// ============================================
function AbaEstoqueGeral({ modelos, loading, onAbrirModelo, onRemover }) {
  const [soDiferenca, setSoDiferenca] = useState(false);
  const visiveis = soDiferenca ? modelos.filter(m => m.diferenca !== 0) : modelos;
  const totalLocal = visiveis.reduce((s, m) => s + m.qtdLocal, 0);
  const totalErp = visiveis.reduce((s, m) => s + m.qtdErp, 0);
  const comDiferenca = modelos.filter(m => m.diferenca !== 0).length;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <p className="text-sm text-gray-600">
          {modelos.length} modelo(s) · <span className={comDiferenca ? 'text-red-700 font-medium' : 'text-green-700'}>{comDiferenca} com diferença</span>
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={soDiferenca} onChange={(e) => setSoDiferenca(e.target.checked)} className="w-4 h-4 text-indigo-600 rounded" />
          Só com diferença
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Modelo</th>
              <th className="text-left px-4 py-3 font-medium">Código</th>
              <th className="text-right px-4 py-3 font-medium">Cobrança (IMEI)</th>
              <th className="text-right px-4 py-3 font-medium">ERP (filial 1)</th>
              <th className="text-right px-4 py-3 font-medium">Diferença</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm">
            {loading && (
              <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600 inline" /></td></tr>
            )}
            {!loading && visiveis.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-gray-500">Nenhum modelo para exibir. Use “Incluir modelo”.</td></tr>
            )}
            {!loading && visiveis.map(m => (
              <tr key={m.ordemProduto} className="hover:bg-indigo-50/40 cursor-pointer" onClick={() => onAbrirModelo(m)}>
                <td className="px-4 py-3 font-medium text-gray-900">
                  {m.nome}{m.inativoErp && <span className="ml-2 text-xs text-amber-700">(inativo no ERP)</span>}
                </td>
                <td className="px-4 py-3 text-gray-600">{m.codigo}</td>
                <td className="px-4 py-3 text-right tabular-nums">{m.qtdLocal}</td>
                <td className="px-4 py-3 text-right tabular-nums">{m.qtdErp}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums ${corDiferenca(m.diferenca)}`}>
                    {m.diferenca > 0 ? `+${m.diferenca}` : m.diferenca}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemover(m); }}
                    title="Remover do acompanhamento" className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {!loading && visiveis.length > 0 && (
            <tfoot className="bg-gray-50 text-sm font-semibold">
              <tr>
                <td className="px-4 py-3" colSpan={2}>Total</td>
                <td className="px-4 py-3 text-right tabular-nums">{totalLocal}</td>
                <td className="px-4 py-3 text-right tabular-nums">{totalErp}</td>
                <td className="px-4 py-3 text-right tabular-nums">{totalLocal - totalErp}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
        Diferença = aparelhos em estoque no cobrança − estoque do ERP. Positivo: sobra no controle por IMEI; negativo: falta.
      </p>
    </div>
  );
}

// ============================================
// ABA VENDAS POR MÊS
// ============================================
function AbaVendasMes() {
  const [meses, setMeses] = useState(12);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async (quantidade) => {
    setLoading(true);
    try {
      const resp = await apiService.celularesEstoque.vendasMensais(quantidade);
      setDados(resp.data.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao carregar vendas por mês.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(meses); }, [meses, carregar]);

  const colunas = dados?.meses ?? [];
  const totais = colunas.map((_, i) => (dados?.modelos ?? []).reduce(
    (acc, m) => ({ erp: acc.erp + m.serie[i].erp, local: acc.local + m.serie[i].local }),
    { erp: 0, local: 0 }
  ));

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200">
        <p className="text-sm text-gray-600">
          Cada célula: <b>ERP</b> / <b className="text-indigo-700">Cobrança</b>. Fundo vermelho quando não batem.
        </p>
        <select value={meses} onChange={(e) => setMeses(Number(e.target.value))} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
          <option value={6}>Últimos 6 meses</option>
          <option value={12}>Últimos 12 meses</option>
          <option value={24}>Últimos 24 meses</option>
        </select>
      </div>

      {loading && <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600 inline" /></div>}

      {!loading && dados && dados.modelos.length === 0 && (
        <p className="py-10 text-center text-gray-500 text-sm">Nenhum modelo acompanhado.</p>
      )}

      {!loading && dados && dados.modelos.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium sticky left-0 bg-gray-50 min-w-[220px]">Modelo</th>
                {colunas.map(c => (
                  <th key={`${c.ano}-${c.mes}`} className="px-3 py-3 font-medium text-center whitespace-nowrap">
                    {NOMES_MES[c.mes - 1]}/{String(c.ano).slice(2)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dados.modelos.map(m => (
                <tr key={m.ordemProduto}>
                  <td className="px-4 py-2 font-medium text-gray-900 sticky left-0 bg-white">{m.nome}</td>
                  {m.serie.map(s => (
                    <CelulaVenda key={`${s.ano}-${s.mes}`} erp={s.erp} local={s.local} />
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td className="px-4 py-2 sticky left-0 bg-gray-50">Total</td>
                {totais.map((t, i) => <CelulaVenda key={i} erp={t.erp} local={t.local} />)}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function CelulaVenda({ erp, local }) {
  const diverge = erp !== local;
  const vazio = erp === 0 && local === 0;
  return (
    <td className={`px-3 py-2 text-center tabular-nums whitespace-nowrap ${diverge ? 'bg-red-50' : ''}`}>
      {vazio ? <span className="text-gray-300">—</span> : (
        <>
          <span className={diverge ? 'text-red-700' : 'text-gray-800'}>{erp}</span>
          <span className="text-gray-400"> / </span>
          <span className="text-indigo-700">{local}</span>
        </>
      )}
    </td>
  );
}

// ============================================
// PÁGINA PRINCIPAL
// ============================================
export default function EstoqueCelulares() {
  const { isGerenteOuAdmin, isAdmin } = useAuth();
  const permitido = isGerenteOuAdmin();

  const [aba, setAba] = useState('estoque');
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalIncluir, setModalIncluir] = useState(false);
  const [modeloAberto, setModeloAberto] = useState(null);
  const [aparelhosModelo, setAparelhosModelo] = useState([]);
  const [statusFiltro, setStatusFiltro] = useState('estoque');
  const [modalCadastro, setModalCadastro] = useState(false);
  const [aparelhoBaixa, setAparelhoBaixa] = useState(null);
  const [buscaImei, setBuscaImei] = useState('');
  const [resultadoImei, setResultadoImei] = useState(null);
  const [saidasPendentes, setSaidasPendentes] = useState([]);

  const carregarModelos = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiService.celularesEstoque.listarModelos();
      setModelos(resp.data.data ?? []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao carregar estoque de celulares.');
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarSaidasPendentes = useCallback(async () => {
    const resp = await apiService.celularesEstoque.listarSaidasPendentes();
    setSaidasPendentes(resp.data.data ?? []);
  }, []);

  const carregarAparelhos = useCallback(async (modelo, status) => {
    try {
      const resp = await apiService.celularesEstoque.listarAparelhos({
        ordemProduto: modelo.ordemProduto, ...(status ? { status } : {}),
      });
      setAparelhosModelo(resp.data.data ?? []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao carregar aparelhos.');
    }
  }, []);

  useEffect(() => {
    if (permitido) carregarModelos();
  }, [permitido, carregarModelos]);

  useEffect(() => {
    if (!permitido) return undefined;
    carregarSaidasPendentes().catch((e) => {
      toast.error(e.response?.data?.error || 'Erro ao carregar saídas pendentes.');
    });
    // Sem toast a cada minuto: a falha aqui fica só no console.
    const intervalo = setInterval(() => {
      carregarSaidasPendentes().catch((e) => console.error('❌ Erro ao atualizar saídas pendentes:', e.message));
    }, INTERVALO_SAIDAS_MS);
    return () => clearInterval(intervalo);
  }, [permitido, carregarSaidasPendentes]);

  useEffect(() => {
    if (modeloAberto) carregarAparelhos(modeloAberto, statusFiltro);
  }, [modeloAberto, statusFiltro, carregarAparelhos]);

  async function recarregarTudo() {
    await carregarModelos();
    await carregarSaidasPendentes().catch((e) => {
      toast.error(e.response?.data?.error || 'Erro ao carregar saídas pendentes.');
    });
    if (modeloAberto) await carregarAparelhos(modeloAberto, statusFiltro);
    if (resultadoImei && buscaImei) await buscarPorImei(buscaImei);
  }

  async function buscarPorImei(termo) {
    try {
      const resp = await apiService.celularesEstoque.listarAparelhos({ imei: termo });
      setResultadoImei(resp.data.data ?? []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao buscar IMEI.');
    }
  }

  function handleBuscarImei(e) {
    e.preventDefault();
    if (buscaImei.replace(/\D/g, '').length < 4) {
      toast.error('Informe ao menos 4 dígitos do IMEI.');
      return;
    }
    buscarPorImei(buscaImei);
  }

  async function handleRemover(modelo) {
    if (!window.confirm(`Remover "${modelo.nome}" do acompanhamento de celulares?`)) return;
    try {
      await apiService.celularesEstoque.removerModelo(modelo.ordemProduto);
      toast.success('Modelo removido do acompanhamento.');
      carregarModelos();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao remover modelo.');
    }
  }

  async function handleEstornar(aparelho) {
    if (!window.confirm(`Estornar a baixa do IMEI ${aparelho.imei}? Ele voltará para o estoque.`)) return;
    try {
      await apiService.celularesEstoque.estornarBaixa(aparelho.id);
      toast.success('Baixa estornada.');
      recarregarTudo();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao estornar baixa.');
    }
  }

  function abrirModelo(modelo) {
    setStatusFiltro('estoque');
    setModeloAberto(modelo);
  }

  if (!permitido) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Acesso restrito</h2>
          <p className="text-gray-600">Esta página é restrita a gerentes e administradores.</p>
        </div>
      </div>
    );
  }

  const abaClass = (nome) => `px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
    aba === nome ? 'border-indigo-600 text-indigo-700 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'
  }`;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-lg shadow-lg p-6 text-white flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Smartphone className="w-8 h-8 text-indigo-200" />
          <div>
            <h1 className="text-2xl font-bold">Estoque de Celulares</h1>
            <p className="text-indigo-100 text-sm mt-1">Controle por IMEI no cobrança × estoque do ERP (filial 1)</p>
          </div>
        </div>
        <button onClick={() => setModalIncluir(true)} className="px-4 py-2 bg-white text-indigo-700 rounded-lg font-medium flex items-center gap-2 hover:bg-indigo-50">
          <Plus className="w-4 h-4" /> Incluir modelo
        </button>
      </div>

      <form onSubmit={handleBuscarImei} className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-2 items-center">
        <Search className="w-4 h-4 text-gray-400" />
        <input
          type="text" value={buscaImei} onChange={(e) => setBuscaImei(e.target.value)}
          placeholder="Localizar aparelho pelo IMEI" className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">Buscar</button>
        {resultadoImei && (
          <button type="button" onClick={() => { setResultadoImei(null); setBuscaImei(''); }} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Limpar</button>
        )}
        {resultadoImei && (
          <div className="w-full mt-2 border-t border-gray-100 pt-2">
            <TabelaAparelhos aparelhos={resultadoImei} mostrarModelo podeEstornar={isAdmin()} onBaixar={setAparelhoBaixa} onEstornar={handleEstornar} />
          </div>
        )}
      </form>

      <div>
        <div className="flex gap-1 border-b border-gray-200">
          <button className={abaClass('estoque')} onClick={() => setAba('estoque')}>Estoque geral</button>
          <button className={abaClass('vendas')} onClick={() => setAba('vendas')}>Vendas por mês</button>
        </div>
        <div className="pt-4">
          {aba === 'estoque'
            ? <AbaEstoqueGeral modelos={modelos} loading={loading} onAbrirModelo={abrirModelo} onRemover={handleRemover} />
            : <AbaVendasMes />}
        </div>
      </div>

      <BlocoSaidasPendentes saidas={saidasPendentes} onConcluida={recarregarTudo} />

      {modeloAberto && (
        <Modal titulo={modeloAberto.nome} onClose={() => setModeloAberto(null)} largura="max-w-3xl">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <select value={statusFiltro} onChange={(e) => setStatusFiltro(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
              <option value="estoque">Em estoque</option>
              <option value="">Todos</option>
              <option value="vendido">Vendidos</option>
              <option value="devolvido">Devolvidos</option>
              <option value="defeito">Defeito</option>
              <option value="outro">Outros</option>
            </select>
            <button onClick={() => setModalCadastro(true)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-indigo-700">
              <PackagePlus className="w-4 h-4" /> Entrada de aparelhos
            </button>
          </div>
          <TabelaAparelhos aparelhos={aparelhosModelo} podeEstornar={isAdmin()} onBaixar={setAparelhoBaixa} onEstornar={handleEstornar} />
        </Modal>
      )}

      {modalIncluir && (
        <ModalIncluirModelo
          onClose={() => setModalIncluir(false)}
          onIncluido={carregarModelos}
          ordensAcompanhadas={new Set(modelos.map(m => m.ordemProduto))}
        />
      )}

      {modalCadastro && modeloAberto && (
        <ModalCadastrarAparelhos
          modelo={modeloAberto}
          onClose={() => setModalCadastro(false)}
          onSalvo={() => { setModalCadastro(false); recarregarTudo(); }}
        />
      )}

      {aparelhoBaixa && (
        <ModalBaixa
          aparelho={aparelhoBaixa}
          onClose={() => setAparelhoBaixa(null)}
          onSalvo={() => { setAparelhoBaixa(null); recarregarTudo(); }}
        />
      )}
    </div>
  );
}
